import type { AppEnvironment, WorkspaceApp } from 'src/api/facade/types'
import type { Application, ApplicationDetail } from 'src/api/main'

const environmentFromInstanceType = (
  type: string | undefined
): AppEnvironment => {
  const t = (type ?? '').toLowerCase()
  if (t.includes('dev') || t.includes('local')) {
    return 'development'
  }
  if (t.includes('stag') || t.includes('preview')) {
    return 'staging'
  }
  return 'production'
}

/**
 * Maps a generated API application to the workspace UI `WorkspaceApp` model.
 * Mock-only fields (`goal`, `owner`, `slug`) are filled with safe placeholders for legacy panels.
 */
export function mapApplicationToWorkspaceApp(
  app: (Application | ApplicationDetail) & {
    // `settings` is on the backend serializer but the codegen drops it: the
    // schema types it as a bare `object` with no `additionalProperties`.
    // Annotate the DRF field as a `DictField`, re-run `npm run openapi`, and
    // remove this widening.
    settings?: Record<string, unknown> | null
  },
  workspaceId: number
): WorkspaceApp {
  const desc = app.description?.trim() ?? ''

  return {
    id: String(app.id),
    workspaceId,
    name: app.name,
    slug: app.app_url || app.domain || String(app.id),
    goal: desc || '—',
    description: desc,
    owner: '—',
    environment: environmentFromInstanceType(app.instance_type?.type),
    status: 'active',
    createdAt: app.date_created,
    domain: app.domain,
    appUrl: app.app_url ?? null,
    iconUrl: app.icon?.file ?? null,
    iconId: app.icon?.id ?? null,
    instanceTypeId: app.instance_type?.id,
    instanceTypeName: app.instance_type?.name,
    instanceTypeDescription: app.instance_type?.description ?? null,
    instanceTypeCode: app.instance_type?.type,
    isPublic: app.is_public,
    settings: app.settings,
    token: app.token,
    relatedRoles: app.related_roles?.map((role) => ({
      id: role.id,
      name: role.name
    })),
    autoProvisionRoleId: app.auto_provision_role ?? null
  }
}
